/**
 * Currency formatting utilities
 */

/**
 * Format a currency value for display
 * @param {string|number} value - The currency value (can be string from backend or number)
 * @param {string} currency - Currency symbol (default: '$')
 * @returns {string} Formatted currency string (e.g., "$12.34")
 */
export const formatCurrency = (value, currency = '$') => {
  if (value === null || value === undefined || value === '') {
    return `${currency}0.00`;
  }
  
  // Convert to number if it's a string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Handle invalid numbers
  if (isNaN(numValue)) {
    return `${currency}0.00`;
  }
  
  // Format to 2 decimal places
  return `${currency}${numValue.toFixed(2)}`;
};

/**
 * Intl-formatted dollars (adds thousands separators: "$12,369.00").
 * #52 sweep: consolidated from ShipmentDrawer's local copy — the app had
 * SIX currency formatters across THREE unit conventions. Use this (or
 * formatCurrencyFromCents) for new code; plain formatCurrency above is
 * the legacy toFixed shape (no separators) kept for its existing callers.
 * @param {number|string} amountInDollars
 */
export const formatCurrencyIntl = (amountInDollars) => {
  if (!amountInDollars && amountInDollars !== 0) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amountInDollars);
};

/**
 * Intl-formatted from CENTS (integer API amounts). Consolidated from
 * PackingSnapshot / ShippingTable locals. The unit is in the NAME on
 * purpose — the scattered copies mixed cents and dollars silently.
 * @param {number} amountInCents
 */
export const formatCurrencyFromCents = (amountInCents) => {
  if (!amountInCents && amountInCents !== 0) return '$0.00';
  const dollars = Math.round(amountInCents) / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(dollars);
};

/**
 * Parse currency string to number
 * @param {string} currencyString - Currency string (e.g., "$12.34" or "12.34")
 * @returns {number} Numeric value
 */
export const parseCurrency = (currencyString) => {
  if (!currencyString) return 0;
  
  // Remove currency symbols and parse
  const cleanString = currencyString.toString().replace(/[$,]/g, '');
  const parsed = parseFloat(cleanString);
  
  return isNaN(parsed) ? 0 : parsed;
};